import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "../lib/utils";
import { isHexColor } from "../utils/color-utils";
import { useFontSet } from "../hooks/useFontSet";
import { useIsPreview } from "../context/PreviewContext";
import { ChevronDown, ChevronUp } from "lucide-react";
export default function OverflowClip({ colors, children }) {
    const isPreview = useIsPreview();
    const wrapperRef = useRef(null);
    const contentRef = useRef(null);
    const [isClipped, setIsClipped] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [resolvedBgColor, setResolvedBgColor] = useState("");
    const { fontSet } = useFontSet();
    const checkOverflow = useCallback(() => {
        if (contentRef.current && !expanded) {
            // max-height is always applied (via shouldClip), so
            // scrollHeight > clientHeight reliably detects overflow
            const overflows = contentRef.current.scrollHeight > contentRef.current.clientHeight + 1;
            setIsClipped(overflows);
        }
    }, [expanded]);
    // Detect overflow on mount and resize — always active
    useEffect(() => {
        checkOverflow();
        const observer = new ResizeObserver(checkOverflow);
        if (contentRef.current) {
            observer.observe(contentRef.current);
        }
        return () => observer.disconnect();
    }, [checkOverflow]);
    // Resolve background color for gradient
    useEffect(() => {
        if (isHexColor(colors.background)) {
            setResolvedBgColor(colors.background);
        }
        else if (contentRef.current) {
            let el = contentRef.current.parentElement;
            while (el) {
                const bg = getComputedStyle(el).backgroundColor;
                if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
                    setResolvedBgColor(bg);
                    break;
                }
                el = el.parentElement;
            }
        }
    }, [colors.background]);
    const handleToggle = () => {
        if (expanded) {
            wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            setExpanded(false);
        }
        else {
            setExpanded(true);
        }
    };
    // Always apply max-height when not expanded — this ensures:
    // 1. scrollHeight > clientHeight works for overflow detection
    // 2. Non-overflowing content is unaffected (fits within 70vh)
    // 3. ResizeObserver catches lazy-loaded images growing past the limit
    const shouldClip = !expanded;
    return (_jsxs("div", { className: "w-full", ref: wrapperRef, children: [_jsxs("div", { className: "relative", children: [_jsx("div", { ref: contentRef, className: cn(shouldClip && "max-h-[calc(70vh-var(--safe-bottom))] overflow-hidden", expanded && "transition-[max-height] duration-500 ease-in-out max-h-[9999px] overflow-visible"), children: children }), isClipped && !expanded && resolvedBgColor && (_jsx("div", { className: "absolute bottom-0 left-0 right-0 h-24 pointer-events-none", style: {
                            background: `linear-gradient(to top, ${resolvedBgColor}, transparent)`,
                        } }))] }), isClipped && (_jsx("div", { className: "flex justify-center pt-4 pb-2", children: _jsx("button", { onClick: isPreview ? undefined : handleToggle, className: cn("flex items-center gap-1 text-sm transition-opacity hover:opacity-70", `font-${fontSet.body}`, !isHexColor(colors.foreground) && `text-${colors.foreground}`), style: isHexColor(colors.foreground)
                        ? { color: colors.foreground }
                        : undefined, children: expanded ? (_jsxs(_Fragment, { children: ["Show less ", _jsx(ChevronUp, { size: 16 })] })) : (_jsxs(_Fragment, { children: ["Show more ", _jsx(ChevronDown, { size: 16 })] })) }) }))] }));
}
